import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Lightbulb } from "lucide-react";

const HabitTipsButton = () => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          className="fixed bottom-6 right-6 z-50 shadow-lg rounded-full px-4 py-2 bg-yellow-50 border-yellow-300 hover:bg-yellow-100 text-yellow-800"
          data-testid="habit-tips-btn"
        >
          <Lightbulb className="w-4 h-4 mr-2" />
          Consigli se vuoi creare nuove abitudini
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-yellow-500" />
            Consigli per creare nuove abitudini
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[65vh] pr-4">
          <div className="space-y-6 text-sm">
            
            {/* Sezione 1 */}
            <section className="space-y-3">
              <h2 className="text-lg font-semibold text-foreground">Non devi tracciare tutto ogni giorno</h2>
              <p className="text-muted-foreground">
                Non serve compilare tutto. Scegli <strong>1–2 aree</strong> e traccia solo quelle. 
                Puoi farlo anche <strong>2–3 volte a settimana</strong>: basta per iniziare a capire i tuoi "schemi".
              </p>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm">
                <p className="font-medium text-blue-800">Esempio</p>
                <ul className="mt-1 text-blue-700 space-y-1">
                  <li>• Martedì e venerdì: segni <strong>Spese + Umore</strong> (2 minuti).</li>
                  <li>• Domenica: guardi com'è andata la settimana e scrivi 1 riga ("quando sono stressato/a spendo di più").</li>
                </ul>
              </div>
            </section>

            {/* Sezione 2 */}
            <section className="space-y-3">
              <h2 className="text-lg font-semibold text-foreground">Se vuoi cambiare davvero qualcosa, fallo più spesso (ma veloce)</h2>
              <p className="text-muted-foreground">
                Se stai cercando di creare una nuova abitudine, aiuta fare un check-in <strong>più regolare</strong>. 
                Non deve essere lungo: anche <strong>30–60 secondi al giorno</strong> vanno bene.
              </p>
              <div className="p-3 bg-green-50 border border-green-200 rounded-sm">
                <p className="font-medium text-green-800">Esempio</p>
                <ul className="mt-1 text-green-700 space-y-1">
                  <li>• Ogni sera: spunti 1 abitudine + colori 1 bisogno + scrivi 1 riga ("cosa ha influito oggi").</li>
                  <li>• Tempo totale: <strong>1 minuto</strong>.</li>
                </ul>
              </div>
            </section>

            {/* Sezione 3 */}
            <section className="space-y-3">
              <h2 className="text-lg font-semibold text-foreground">Meno obiettivi insieme = più probabilità di riuscirci</h2>
              <p className="text-muted-foreground">
                Se provi a cambiare troppe cose tutte insieme, ti stanchi e molli. Meglio <strong>poche cose</strong>, fatte bene.
              </p>
              <div className="p-3 bg-red-50 border border-red-200 rounded-sm">
                <p className="font-medium text-red-800">Esempio</p>
                <p className="mt-1 text-red-700">
                  Invece di: palestra + dieta + sveglia presto + meditazione + studio…<br/>
                  Fai: per 4–8 settimane lavori su <strong>una cosa sola</strong> (es. palestra).
                </p>
              </div>
            </section>

            {/* Regola operativa */}
            <section className="space-y-3">
              <h2 className="text-lg font-semibold text-foreground">Regola operativa generale</h2>
              <div className="p-4 bg-yellow-50 border-2 border-yellow-300 rounded-sm">
                <ul className="space-y-2 text-yellow-800">
                  <li>• <strong>1 area di focus</strong> (es. sonno, energia)</li>
                  <li>• <strong>1 obiettivo principale</strong></li>
                  <li>• Fino a <strong>2 micro-abitudini</strong> (piccole e facili)</li>
                </ul>
                <p className="mt-3 text-sm text-yellow-700">
                  Il resto lo metti in <strong>"In arrivo"</strong> (non lo perdi: lo farai dopo).
                </p>
              </div>
            </section>

            {/* Esempi pratici */}
            <section className="space-y-4">
              <h2 className="text-lg font-semibold text-foreground">Esempi pratici</h2>
              
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-sm">
                <p className="font-medium text-purple-800">Esempio 1: Stress / calma</p>
                <ul className="mt-2 text-purple-700 text-sm space-y-1">
                  <li><strong>Area:</strong> Stress</li>
                  <li><strong>Obiettivo:</strong> "Gestire meglio lo stress nei giorni pieni"</li>
                  <li><strong>Micro-abitudini (max 2):</strong></li>
                  <li className="ml-4">• Respiro 1 minuto (prima di iniziare a lavorare)</li>
                  <li className="ml-4">• Scarico mentale 3 righe (prima di dormire)</li>
                </ul>
              </div>

              <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-sm">
                <p className="font-medium text-indigo-800">Esempio 2: Energia / sonno</p>
                <ul className="mt-2 text-indigo-700 text-sm space-y-1">
                  <li><strong>Area:</strong> Energia</li>
                  <li><strong>Obiettivo:</strong> "Dormire meglio"</li>
                  <li><strong>Micro-abitudini (max 2):</strong></li>
                  <li className="ml-4">• Schermi off 20 minuti prima di dormire</li>
                  <li className="ml-4">• Leggo 10 minuti</li>
                </ul>
              </div>

              <div className="p-3 bg-teal-50 border border-teal-200 rounded-sm">
                <p className="font-medium text-teal-800">Esempio 3: Movimento (per chi "non ha tempo")</p>
                <ul className="mt-2 text-teal-700 text-sm space-y-1">
                  <li><strong>Area:</strong> Movimento</li>
                  <li><strong>Obiettivo:</strong> "Muovermi con più regolarità"</li>
                  <li><strong>Micro-abitudini (max 2):</strong></li>
                  <li className="ml-4">• Camminata 10 minuti (3 volte a settimana)</li>
                  <li className="ml-4">• Stretching 2 minuti quando torno a casa</li>
                </ul>
              </div>
            </section>

            {/* Cicli e Review */}
            <section className="space-y-3">
              <h2 className="text-lg font-semibold text-foreground">Lavora a cicli e fai una piccola review</h2>
              <p className="text-muted-foreground">
                Pensa a periodi di <strong>4–8 settimane</strong>: costruisci, stabilizzi, poi aggiungi. 
                Ogni settimana basta una mini review.
              </p>
              
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-sm space-y-3">
                <p className="font-medium text-orange-800">Mini review (1 minuto)</p>
                <div className="space-y-2 text-sm text-orange-700">
                  <div>
                    <p className="font-medium">Cosa ha funzionato?</p>
                    <p className="text-xs">Se faccio la camminata subito dopo lavoro, la faccio davvero. Stretching dopo la doccia è facile perché è "attaccato" a una routine.</p>
                  </div>
                  <div>
                    <p className="font-medium">Quali sono stati gli ostacoli?</p>
                    <p className="text-xs">Quando piove o fa freddo salto. Se torno tardi, mi manca la voglia.</p>
                  </div>
                  <div>
                    <p className="font-medium">Cosa posso fare per superarli?</p>
                    <p className="text-xs">Piano B: in caso di pioggia faccio 10 minuti in casa (scale / passo sul posto). Se torno tardi: faccio solo 2 minuti di stretching e basta.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Esempio Studio */}
            <section className="space-y-3">
              <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-sm">
                <p className="font-medium text-cyan-800">Esempio: Studio</p>
                <ul className="mt-2 text-cyan-700 text-sm space-y-1">
                  <li><strong>Area:</strong> Studio</li>
                  <li><strong>Obiettivo:</strong> "Studiare con più costanza e meno stress"</li>
                  <li><strong>Micro-abitudini (max 2):</strong></li>
                  <li className="ml-4">• 25 minuti di studio (Pomodoro) 5 giorni a settimana</li>
                  <li className="ml-4">• 5 minuti di ripasso a fine sessione (solo 3 punti chiave)</li>
                </ul>
                <div className="mt-3 pt-3 border-t border-cyan-200">
                  <p className="text-xs text-cyan-600">
                    <strong>Cosa ha funzionato:</strong> Studiare sempre alla stessa ora evita di rimandare.<br/>
                    <strong>Ostacoli:</strong> Inizio tardi perché apro il telefono.<br/>
                    <strong>Soluzione:</strong> Metto il telefono in un'altra stanza.
                  </p>
                </div>
              </div>
            </section>

            {/* Riferimenti */}
            <section className="space-y-3 pt-4 border-t border-border">
              <p className="text-sm text-foreground font-semibold">Riferimenti scientifici</p>
              <ul className="text-xs text-muted-foreground space-y-2">
                <li>
                  Carver, C.S. & Scheier, M.F. (1998). <em>On the Self-Regulation of Behavior.</em> Cambridge University Press.{" "}
                  <a href="https://doi.org/10.1017/CBO9781139174794" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                    https://doi.org/10.1017/CBO9781139174794
                  </a>
                </li>
                <li>
                  Gardner, B., Lally, P., & Wardle, J. (2012). Making health habitual: the psychology of 'habit-formation' and general practice. <em>British Journal of General Practice.</em>{" "}
                  <a href="https://doi.org/10.3399/bjgp12X659466" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                    https://doi.org/10.3399/bjgp12X659466
                  </a>
                </li>
                <li>
                  Gollwitzer, P.M. & Sheeran, P. (2006). Implementation intentions and goal achievement: A meta-analysis. <em>Advances in Experimental Social Psychology.</em>{" "}
                  <a href="https://doi.org/10.1016/S0065-2601(06)38002-1" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                    https://doi.org/10.1016/S0065-2601(06)38002-1
                  </a>
                </li>
                <li>
                  Harkin, B., et al. (2016). Does monitoring goal progress promote goal attainment? A meta-analysis. <em>Psychological Bulletin.</em>{" "}
                  <a href="https://doi.org/10.1037/bul0000025" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                    https://doi.org/10.1037/bul0000025
                  </a>
                </li>
                <li>
                  Keller, J., et al. (2021). Habit formation following routine-based versus time-based cue planning. <em>Frontiers in Psychology.</em>{" "}
                  <a href="https://doi.org/10.3389/fpsyg.2021.653512" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                    https://doi.org/10.3389/fpsyg.2021.653512
                  </a>
                </li>
                <li>
                  Kwasnicka, D., et al. (2016). Theoretical explanations for maintenance of behaviour change. <em>Health Psychology Review.</em>{" "}
                  <a href="https://doi.org/10.1080/17437199.2016.1151372" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                    https://doi.org/10.1080/17437199.2016.1151372
                  </a>
                </li>
                <li>
                  Lally, P., et al. (2010). How are habits formed: Modelling habit formation in the real world. <em>European Journal of Social Psychology.</em>{" "}
                  <a href="https://doi.org/10.1002/ejsp.674" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                    https://doi.org/10.1002/ejsp.674
                  </a>
                </li>
                <li>
                  Locke, E.A. & Latham, G.P. (2002). Building a practically useful theory of goal setting. <em>American Psychologist.</em>{" "}
                  <a href="https://doi.org/10.1037/0003-066X.57.9.705" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                    https://doi.org/10.1037/0003-066X.57.9.705
                  </a>
                </li>
                <li>
                  Rothman, A.J., et al. (2015). Reflective and automatic processes in the initiation and maintenance of dietary change. <em>Annals of Behavioral Medicine.</em>{" "}
                  <a href="https://doi.org/10.1016/j.paid.2015.05.011" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                    https://doi.org/10.1016/j.paid.2015.05.011
                  </a>
                </li>
                <li>
                  Wood, W. & Neal, D.T. (2007). A new look at habits and the habit-goal interface. <em>Psychological Review.</em>{" "}
                  <a href="https://doi.org/10.1086/664500" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                    https://doi.org/10.1086/664500
                  </a>
                </li>
                <li>
                  Wood, W. & Rünger, D. (2016). Psychology of Habit. <em>Annual Review of Psychology.</em>{" "}
                  <a href="https://doi.org/10.1002/job.1963" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                    https://doi.org/10.1002/job.1963
                  </a>
                </li>
                <li>
                  Zimmerman, B.J. (2002). Becoming a self-regulated learner: An overview. <em>Theory Into Practice.</em>{" "}
                  <a href="https://doi.org/10.4025/reveducfis.v24i2.17524" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                    https://doi.org/10.4025/reveducfis.v24i2.17524
                  </a>
                </li>
              </ul>
            </section>

          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default HabitTipsButton;
